import { inject, Injectable, signal } from '@angular/core';
import { AlertController, NavController } from '@ionic/angular/standalone';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { PushContent } from '../models/push-content.model';
import { AppRoutes } from '../enums/app-routes.enum';
import { buildTabRoute } from '../utils/route.util';
import { ChatUnreadService } from './chat-unread.service';
import { environment } from '../../environments/environment';

const PUSH_TYPE = {
  DOMANDA_CASO_CLINICO: 'DOMANDA_CASO_CLINICO',
  SURVEY: 'SURVEY',
  CONGRESS_GROUPS: 'CONGRESS_GROUPS',
  RISPOSTA_HELP_DESK: 'RISPOSTA_HELP_DESK',
  GENERICA: 'GENERICA',
  CHAT_MESSAGE: 'CHAT_MESSAGE',
} as const;

const DEDUP_TTL_MS = 5000;
const CHAT_BADGE_REFRESH_DEBOUNCE_MS = 2000;
const SURVEY_TOAST_DEBOUNCE_MS = 800;

interface ActiveChatAlert {
  alert: HTMLIonAlertElement;
  count: number;
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly navCtrl = inject(NavController);
  private readonly alertCtrl = inject(AlertController);
  private readonly chatUnread = inject(ChatUnreadService);

  /** Chat room attualmente visualizzata dall'utente */
  readonly activeChatRoomId = signal<string | null>(null);

  /** Fingerprint di eventi gia visti (deduplicazione push/WS) */
  private readonly seenEvents = new Map<string, number>();

  /** Alert chat attivi raggruppati per chatRoomId */
  private readonly activeChatAlerts = new Map<string, ActiveChatAlert>();

  /** Timeout pendenti per debounce push SURVEY */
  private readonly pendingSurveyTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  /** Trigger refresh badge chat con debounce */
  private readonly chatUnreadRefresh$ = new Subject<void>();

  constructor() {
    this.chatUnreadRefresh$
      .pipe(debounceTime(CHAT_BADGE_REFRESH_DEBOUNCE_MS))
      .subscribe(() => this.chatUnread.refresh());
  }

  handlePush(push: PushContent): void {
    // In foreground il canale primario e' il WebSocket: la push e' ridondante
    // e va scartata a monte (nessun alert, nessun side-effect sul dedup).
    if (push.trigger === 'foreground') return;

    const type = push.data?.['type'] as string | undefined;
    const title = push.title ?? '';
    const body = push.body ?? '';

    // Dedup universale: copre il caso iOS che consegna la stessa push sia in
    // willPresent sia in didReceive (tap utente) e difende tutti i tipi
    // (non solo SURVEY).
    // La logica SURVEY-specific (handleSurvey con debounce 800ms) resta per
    // la dedup push/WS.
    const fp = this.computeFingerprint(push);
    if (type !== PUSH_TYPE.SURVEY) {
      if (this.isSeen(fp)) return;
      this.markAsSeen(fp);
    }

    switch (type) {
      case PUSH_TYPE.SURVEY:
        this.handleSurvey(title, body, push.data);
        break;

      case PUSH_TYPE.CHAT_MESSAGE:
        this.handleChatMessage(title, body, push.data);
        break;

      case PUSH_TYPE.DOMANDA_CASO_CLINICO:
        this.showAlertWithNav(title, body, buildTabRoute(AppRoutes.HOME));
        break;

      case PUSH_TYPE.CONGRESS_GROUPS:
        this.showAlertWithNav(title, body, buildTabRoute(AppRoutes.HOME));
        break;

      case PUSH_TYPE.RISPOSTA_HELP_DESK:
        this.showAlertWithNav(title, body, buildTabRoute(AppRoutes.HOME));
        break;

      case PUSH_TYPE.GENERICA:
        this.handleGenerica(title, body, push.data);
        break;

      default:
        this.showAlert(title, body);
        break;
    }
  }

  private computeFingerprint(push: PushContent): string {
    const type = (push.data?.['type'] as string | undefined) ?? 'UNK';
    const title = push.title ?? '';
    const body = push.body ?? '';
    const data = push.data ?? {};

    switch (type) {
      case PUSH_TYPE.SURVEY: {
        const surveyId = (data['occurrenceSurveyId'] as string) ?? '';
        return this.surveyFingerprint(surveyId);
      }
      case PUSH_TYPE.CHAT_MESSAGE: {
        const roomId = (data['chatRoomId'] as string) ?? 'NO_ROOM';
        return `CHAT:${roomId}|${title}|${body}`;
      }
      case PUSH_TYPE.DOMANDA_CASO_CLINICO:
        return `QA:${title}|${body}`;
      case PUSH_TYPE.CONGRESS_GROUPS: {
        const congressId = (data['congressId'] as string) ?? '';
        return `CG:${congressId}|${title}|${body}`;
      }
      case PUSH_TYPE.RISPOSTA_HELP_DESK:
        return `HD:${title}|${body}`;
      case PUSH_TYPE.GENERICA: {
        const congressId = (data['congressId'] as string) ?? '';
        const homeDataCode = (data['homeDataCode'] as string) ?? '';
        return `GEN:${congressId}|${homeDataCode}|${title}|${body}`;
      }
      default:
        return `UNK:${title}|${body}`;
    }
  }

  // --- Deduplicazione ---

  markAsSeen(fingerprint: string): void {
    this.seenEvents.set(fingerprint, Date.now());
    setTimeout(() => this.seenEvents.delete(fingerprint), DEDUP_TTL_MS);
  }

  isSeen(fingerprint: string): boolean {
    const ts = this.seenEvents.get(fingerprint);
    if (ts == null) return false;
    if (Date.now() - ts > DEDUP_TTL_MS) {
      this.seenEvents.delete(fingerprint);
      return false;
    }
    return true;
  }

  /**
   * Imposta la chat room attiva. Se esiste un alert raggruppato per quella room,
   * viene dismissato (l'utente ora e nella room, l'alert e obsoleto).
   */
  setActiveChatRoom(roomId: string | null): void {
    this.activeChatRoomId.set(roomId);
    if (roomId) {
      const entry = this.activeChatAlerts.get(roomId);
      if (entry) {
        entry.alert.dismiss().catch(() => { /* already dismissed */ });
        this.activeChatAlerts.delete(roomId);
      }
    }
  }

  // --- Handlers per tipo ---

  private surveyFingerprint(surveyId: string): string {
    return surveyId ? `SURVEY:${surveyId}` : `SURVEY:${environment.congressId}`;
  }

  private handleSurvey(title: string, body: string, data?: Record<string, unknown>): void {
    const surveyId = (data?.['occurrenceSurveyId'] as string) ?? '';
    const fp = this.surveyFingerprint(surveyId);

    if (this.isSeen(fp)) return;

    const existing = this.pendingSurveyTimeouts.get(fp);
    if (existing) clearTimeout(existing);

    const timeout = setTimeout(() => {
      this.pendingSurveyTimeouts.delete(fp);
      if (this.isSeen(fp)) return;
      this.showAlertWithNav(title, body, buildTabRoute(AppRoutes.INSIGHTS));
    }, SURVEY_TOAST_DEBOUNCE_MS);

    this.pendingSurveyTimeouts.set(fp, timeout);
  }

  private handleChatMessage(title: string, body: string, data?: Record<string, unknown>): void {
    const chatRoomId = (data?.['chatRoomId'] as string) ?? '';

    if (chatRoomId && this.activeChatRoomId() === chatRoomId) return;

    this.chatUnreadRefresh$.next();

    const route = chatRoomId
      ? buildTabRoute(AppRoutes.CHAT, chatRoomId)
      : buildTabRoute(AppRoutes.CHAT);

    if (chatRoomId) {
      this.presentChatAlert(chatRoomId, title, body, route);
    } else {
      this.showAlertWithNav(title, body, route, 'Rispondi');
    }
  }

  private handleGenerica(title: string, body: string, data?: Record<string, unknown>): void {
    const congressId = (data?.['congressId'] as string) ?? '';
    const homeDataCode = (data?.['homeDataCode'] as string) ?? '';

    if (congressId && homeDataCode) {
      this.showAlertWithNav(title, body, buildTabRoute(AppRoutes.HOME));
    } else {
      this.showAlert(title, body);
    }
  }

  // --- Alert helpers ---

  /**
   * Grouping alert chat per chatRoomId: se un alert e' gia' visibile per la stessa
   * room, aggiorniamo il conteggio interno. Non riapriamo un nuovo alert sopra
   * uno aperto per evitare flicker di overlay Ionic.
   */
  private async presentChatAlert(
    chatRoomId: string,
    title: string,
    body: string,
    route: string,
  ): Promise<void> {
    const existing = this.activeChatAlerts.get(chatRoomId);

    if (existing) {
      existing.count += 1;
      existing.alert.message = `${existing.count} nuovi messaggi`;
      return;
    }

    const alert = await this.alertCtrl.create({
      header: title,
      message: body,
      cssClass: 'light-alert',
      backdropDismiss: true,
      buttons: [
        {
          text: 'Ignora',
          role: 'cancel',
        },
        {
          text: 'Rispondi',
          handler: () => {
            this.navCtrl.navigateForward(route);
          },
        },
      ],
    });

    this.activeChatAlerts.set(chatRoomId, { alert, count: 1 });

    alert.onDidDismiss().then(() => {
      const current = this.activeChatAlerts.get(chatRoomId);
      if (current?.alert === alert) {
        this.activeChatAlerts.delete(chatRoomId);
      }
    });

    await alert.present();
  }

  private async showAlertWithNav(
    title: string,
    body: string,
    route: string,
    actionLabel: string = 'Vai',
  ): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: title,
      message: body,
      cssClass: 'light-alert',
      backdropDismiss: true,
      buttons: [
        {
          text: 'Ignora',
          role: 'cancel',
        },
        {
          text: actionLabel,
          handler: () => {
            this.navCtrl.navigateForward(route);
          },
        },
      ],
    });
    await alert.present();
  }

  private async showAlert(title: string, body: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: title,
      message: body,
      cssClass: 'light-alert',
      backdropDismiss: true,
      buttons: [{ text: 'OK', role: 'cancel' }],
    });
    await alert.present();
  }
}
